import type { ShopperProducts } from '@/scapi';
import { getMenuOverrideItems, isMenuOverridden, type MenuItemListOverrides } from './menu-item-list-overrides';
import { getCategorySubmenuSlot, type CategorySubmenuSlots } from './category-submenu-slots';
import { createCategoryUrl } from './hierarchy-url';

export type CategoryHrefInput = Pick<ShopperProducts.schemas['Category'], 'id'> & {
    c_hierarchyUrl?: string;
};

/** Max width for all burger menu Page Designer regions inside the drawer. */
export const BURGER_MENU_CONTENT_SLOTS_MAX_WIDTH_CLASS = 'w-full max-w-[390px]';

/**
 * Burger menu embedded host region that holds the Page Designer Trending Carousel rendered
 * at the top of the burger menu.
 */
export const GLOBAL_TRENDING_CAROUSEL_REGION_ID = 'trendingCarousel';

/**
 * Burger menu embedded host region for the Burger Menu Footer container at the bottom of the drawer.
 */
export const GLOBAL_BURGER_MENU_FOOTER_REGION_ID = 'burgerMenuFooter';

export function isCategoryShownInMenu(category: ShopperProducts.schemas['Category']): boolean {
    return category.c_showInMenu === true;
}

export function getMenuVisibleChildCategories(
    category: ShopperProducts.schemas['Category']
): ShopperProducts.schemas['Category'][] {
    return category.categories?.filter(isCategoryShownInMenu) ?? [];
}

export function filterCategoryTreeShownInMenu(
    category: ShopperProducts.schemas['Category']
): ShopperProducts.schemas['Category'] {
    const visibleChildren = getMenuVisibleChildCategories(category).map(filterCategoryTreeShownInMenu);

    if (visibleChildren.length === 0) {
        const categoryWithoutChildren = { ...category };
        delete categoryWithoutChildren.categories;
        return categoryWithoutChildren;
    }

    return {
        ...category,
        categories: visibleChildren,
    };
}

export function filterCategoriesTreeShownInMenu(
    categories: ShopperProducts.schemas['Category'][]
): ShopperProducts.schemas['Category'][] {
    return categories.filter(isCategoryShownInMenu).map(filterCategoryTreeShownInMenu);
}

export function hasSubcategories(category: ShopperProducts.schemas['Category']): boolean {
    return getMenuVisibleChildCategories(category).length > 0;
}

/**
 * Merges deferred burger-menu navigation categories (level-2 SCAPI fetch) over the shallow
 * root entries from the app layout loader so submenus can resolve browsable children.
 */
export function mergeDeferredBurgerMenuCategories(
    rootCategories: ShopperProducts.schemas['Category'][],
    deferredCategories?: ShopperProducts.schemas['Category'][]
): ShopperProducts.schemas['Category'][] {
    if (!deferredCategories?.length) {
        return filterCategoriesTreeShownInMenu(rootCategories);
    }

    const deferredById = new Map(deferredCategories.map((category) => [category.id, category]));

    return filterCategoriesTreeShownInMenu(rootCategories.map((category) => deferredById.get(category.id) ?? category));
}

export function hasBrowsableChildren(
    category: ShopperProducts.schemas['Category'],
    menuOverrides: MenuItemListOverrides
): boolean {
    if (isMenuOverridden(category) && getMenuOverrideItems(category.id, menuOverrides)) {
        return true;
    }

    return hasSubcategories(category);
}

export function getCategoryHref(category: CategoryHrefInput): string {
    return createCategoryUrl(category);
}

export { getCategorySubmenuSlot, type CategorySubmenuSlots };
export { isMenuOverridden, type MenuItemListOverrides, type MenuItemOverrideEntry } from './menu-item-list-overrides';
